/**
 * Text Pattern Matching Utilities
 * 
 * Shared patterns for detecting and highlighting various text patterns in messages.
 * This file serves as a single source of truth for both web and mobile clients.
 * 
 * Patterns included:
 * - Email addresses
 * - URLs (with smart file extension detection)
 * - Phone numbers
 * - Mentions (@username)
 * - Hashtags (#tag)
 * - Stock tickers ($TICKER)
 * 
 * IMPORTANT: Android Compatibility Note
 * JavaScriptCore on Android does not support certain regex features like lookbehind assertions (?<!...).
 * All patterns in this file are Android-compatible and avoid:
 * 1. Lookbehind assertions (?<!...)
 * 2. Named capture groups (?<name>...)
 * Instead, edge cases are handled in the processing logic after matching.
 */

/**
 * Regex patterns for text matching
 * 
 * All patterns use the global flag (g) for finding multiple matches in text.
 */
export const TEXT_PATTERNS = {
    /**
     * Email pattern: matches standard email addresses
     * Example: user@example.com
     */
    email: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9][a-zA-Z0-9-]*\.[a-zA-Z]+/g,

    /**
     * URL pattern with smart file extension detection
     * 
     * Features:
     * 1. Matches full URLs with protocol (https?://...) - always valid, captures everything
     * 2. Matches URLs without protocol but with proper domain structure
     * 3. Avoids matching standalone file extensions (e.g., "example.pdf", "14.4.2.ogg")
     * 4. Still matches URLs with file extensions (e.g., "example.com/file.pdf")
     * 
     * Edge cases handled:
     * - Standalone files: "file.pdf", "document.docx", "14.4.2.ogg" - NOT matched
     *   * Pattern requires domain segments with letters (excludes "14.4.2.ogg")
     *   * Negative lookahead excludes common file extensions when no path (excludes "example.pdf")
     * - Version numbers: "v1.2.3", "14.4.2" - NOT matched (all numeric segments, no TLD)
     * - File paths: "/path/file.pdf", "C:\file.pdf" - NOT matched (starts with / or \)
     * - IP addresses: "192.168.1.1" - NOT matched (all numeric segments, no letters)
     * - URLs with files: "example.com/file.pdf" - MATCHED (has domain + path)
     * - Single domains: "example.com" - MATCHED (valid domain, not a file extension)
     * - Single-letter domains: "x.com", "a.com" - MATCHED (valid single-letter domains)
     * - Multiple segments: "www.example.com" - MATCHED (clearly a domain)
     * 
     * Pattern breakdown:
     * - https?:\/\/[^\s]+ : Full URLs with protocol (always valid, includes paths/files)
     * - For URLs without protocol:
     *   - (?:(?:[a-zA-Z][a-zA-Z0-9-]*[a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z][a-zA-Z0-9-]*|[a-zA-Z])\.)+ : Domain segments (must contain at least one letter, allows single letters like "x")
     *   - (?!(?:pdf|docx?|xlsx?|pptx?|jpg|jpeg|png|gif|bmp|svg|webp|mp[34]|avi|mov|ogg|wav|flac|zip|rar|tar|gz|7z|txt|csv|json|xml|html|css|js|ts|py|java|cpp|exe|dmg|pkg)(?:\s|$)) : Negative lookahead
     *     * Excludes common file extensions when no path (pdf, doc, etc.)
     *   - [a-zA-Z]{2,} : TLD (2+ letters)
     *   - (?:\/[^\s]*)? : Optional path and query params (allows "example.com/file.pdf")
     * 
     * Note: We exclude URLs starting with $ to avoid matching stock tickers
     * The $ prefix check is handled in processing logic since we can't use lookbehind on Android
     */
    url: /(?:https?:\/\/[^\s]+|(?:(?:[a-zA-Z][a-zA-Z0-9-]*[a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z][a-zA-Z0-9-]*|[a-zA-Z])\.)+(?!(?:pdf|docx?|xlsx?|pptx?|jpg|jpeg|png|gif|bmp|svg|webp|mp[34]|avi|mov|ogg|wav|flac|zip|rar|tar|gz|7z|txt|csv|json|xml|html|css|js|ts|py|java|cpp|exe|dmg|pkg)(?:\s|$))[a-zA-Z]{2,}(?:\/[^\s]*)?)/g,

    /**
     * Phone number pattern: matches phone numbers with optional country code
     * Example: +1234567890, 1234567890
     */
    phone: /(\+?\d{9,15})/g,

    /**
     * Mention pattern: matches @username mentions
     * Example: @username
     * Excludes mentions that are part of URLs (handled in processing logic)
     */
    mention: /@[\w.-]+(?![^\s]*\.[^\s]*)/g,

    /**
     * Hashtag pattern: matches #hashtags
     * Example: #hashtag
     */
    hashtag: /(#[a-zA-Z0-9_]+)/g,

    /**
     * Stock ticker pattern: matches ticker-like symbols, not currency amounts
     * Example: $AAPL, $TSLA, $^GSPC, $NYSE:T
     *
     * Heuristic:
     * - Requires the symbol body to start with a letter after an optional exchange prefix or ^
     * - This intentionally skips money values like $100, $100k, or $25.50
     */
    stockTicker: /\$(?:[A-Z]+:|\^)?[a-zA-Z][a-zA-Z0-9]*(?:[-=.][A-Z0-9]{1,4})?/g
};

const MARKDOWN_LINK_PATTERN = /\[([^\]]+)\]\(([^)]+)\)/g;
const NON_HTTP_PROTOCOL_URL_PATTERN = /(?:[a-zA-Z][a-zA-Z0-9+.-]*):\/\/[^\s]+/g;
const PROTOCOL_URL_CONTEXT_PATTERN = /(?:[a-zA-Z][a-zA-Z0-9+.-]*):\/\/[^\s]*$/;

const isStringPart = part => typeof part === 'string';

const splitStringParts = (parts, splitFn) =>
    parts.flatMap(part => (isStringPart(part) ? splitFn(part) : part));

const createTextToken = text => ({
    type: 'text',
    text
});

const createToken = (type, text, extra = null) => ({
    type,
    text,
    ...(extra || {})
});

const isProtocolUrlContext = value =>
    typeof value === 'string' && PROTOCOL_URL_CONTEXT_PATTERN.test(value);

/**
 * Tokenize interactive text using the shared TEXT_PATTERNS order plus a few
 * guardrails for markdown links and non-http protocol URLs.
 */
export const tokenizeInteractiveText = text => {
    if (!text) {
        return null;
    }

    let parts = [String(text)];

    parts = splitStringParts(parts, part => {
        const splits = [];
        let lastIndex = 0;
        let match;

        MARKDOWN_LINK_PATTERN.lastIndex = 0;
        while ((match = MARKDOWN_LINK_PATTERN.exec(part)) !== null) {
            if (lastIndex < match.index) {
                splits.push(part.substring(lastIndex, match.index));
            }

            const [fullMatch, linkText, url] = match;
            splits.push(createToken('markdownLink', linkText, { url }));
            lastIndex = match.index + fullMatch.length;
        }

        if (lastIndex < part.length) {
            splits.push(part.substring(lastIndex));
        }

        return splits;
    });

    parts = splitStringParts(parts, part => {
        const splits = [];
        let lastIndex = 0;
        let match;

        NON_HTTP_PROTOCOL_URL_PATTERN.lastIndex = 0;
        while ((match = NON_HTTP_PROTOCOL_URL_PATTERN.exec(part)) !== null) {
            const matchedValue = match[0];
            if (/^https?:\/\//i.test(matchedValue)) {
                continue;
            }

            // Emit compound protocols (git+ssh://, svn+ssh://, etc.) as text tokens
            // so later steps (URL, hashtag, email) don't pick up sub-patterns inside them
            const protocol = matchedValue.split('://')[0];
            if (protocol.includes('+')) {
                if (lastIndex < match.index) {
                    splits.push(part.substring(lastIndex, match.index));
                }
                splits.push(createTextToken(matchedValue));
                lastIndex = match.index + matchedValue.length;
                continue;
            }

            if (lastIndex < match.index) {
                splits.push(part.substring(lastIndex, match.index));
            }

            splits.push(createToken('protocolUrl', matchedValue));
            lastIndex = match.index + matchedValue.length;
        }

        if (lastIndex < part.length) {
            splits.push(part.substring(lastIndex));
        }

        return splits;
    });

    parts = splitStringParts(parts, part => {
        const flags = TEXT_PATTERNS.stockTicker.flags.replace('g', '');
        const stockTickerRegex = new RegExp(`(${TEXT_PATTERNS.stockTicker.source})`, flags);
        const splits = part.split(stockTickerRegex);
        return splits.map((split, index) =>
            index % 2 === 1 ? createToken('stockTicker', split) : split
        );
    });

    parts = splitStringParts(parts, part => {
        const flags = TEXT_PATTERNS.email.flags.replace('g', '');
        const emailRegex = new RegExp(`(${TEXT_PATTERNS.email.source})`, flags);
        const splits = part.split(emailRegex);
        return splits.map((split, index) => {
            if (index % 2 !== 1) {
                return split;
            }

            const prevText = index > 0 ? splits[index - 1] : '';
            return isProtocolUrlContext(prevText) ? split : createToken('email', split);
        });
    });

    parts = splitStringParts(parts, part => {
        const flags = TEXT_PATTERNS.url.flags.replace('g', '');
        const urlRegex = new RegExp(`(${TEXT_PATTERNS.url.source})`, flags);
        const splits = part.split(urlRegex);
        return splits.map((split, index) => {
            if (index % 2 !== 1) {
                return split;
            }

            const prevText = index > 0 ? splits[index - 1] : '';
            if (
                split.startsWith('$') ||
                (typeof prevText === 'string' && prevText.endsWith('$'))
            ) {
                return split;
            }

            if (
                typeof prevText === 'string' &&
                (prevText.endsWith('/') || prevText.endsWith('\\'))
            ) {
                return split;
            }

            return createToken('url', split);
        });
    });

    parts = splitStringParts(parts, part => {
        const flags = TEXT_PATTERNS.phone.flags.replace('g', '');
        const phoneRegex = new RegExp(TEXT_PATTERNS.phone.source, flags);
        const splits = part.split(phoneRegex);
        return splits.map((split, index) =>
            index % 2 === 1 ? createToken('phone', split) : split
        );
    });

    parts = splitStringParts(parts, part => {
        const flags = TEXT_PATTERNS.mention.flags.replace('g', '');
        const mentionRegex = new RegExp(`(${TEXT_PATTERNS.mention.source})`, flags);
        const splits = part.split(mentionRegex);
        return splits.map((split, index) => {
            if (index % 2 !== 1) {
                return split;
            }

            const prevText = index > 0 ? splits[index - 1] : '';
            return isProtocolUrlContext(prevText)
                ? split
                : createToken('mention', split);
        });
    });

    parts = splitStringParts(parts, part => {
        const splits = [];
        let lastIndex = 0;
        let match;

        const hashtagRegex = new RegExp(
            TEXT_PATTERNS.hashtag.source,
            TEXT_PATTERNS.hashtag.flags
        );
        hashtagRegex.lastIndex = 0;
        while ((match = hashtagRegex.exec(part)) !== null) {
            if (lastIndex < match.index) {
                splits.push(part.substring(lastIndex, match.index));
            }

            splits.push(createToken('hashtag', match[0]));
            lastIndex = match.index + match[0].length;
        }

        if (lastIndex < part.length) {
            splits.push(part.substring(lastIndex));
        }

        return splits.length > 0 ? splits : [part];
    });

    return parts.map(part => (isStringPart(part) ? createTextToken(part) : part));
};

// Backwards-compatible alias while call sites migrate off the title-specific name.
export const tokenizeTitleInteractiveText = tokenizeInteractiveText;

/**
 * Get all text patterns as an object
 * Useful for destructuring or iterating over patterns
 * 
 * @returns {Object} Object containing all text pattern regexes
 */
export const getTextPatterns = () => {
    return TEXT_PATTERNS;
};

/**
 * Get a specific pattern by name
 * 
 * @param {string} patternName - Name of the pattern to retrieve
 * @returns {RegExp|null} The requested pattern or null if not found
 */
export const getPattern = (patternName) => {
    return TEXT_PATTERNS[patternName] || null;
};

/**
 * Check if a text matches a specific pattern
 * 
 * @param {string} text - Text to check
 * @param {string} patternName - Name of the pattern to use
 * @returns {boolean} True if the text matches the pattern
 */
export const matchesPattern = (text, patternName) => {
    const pattern = getPattern(patternName);
    if (!pattern) return false;
    
    // Reset regex lastIndex to ensure consistent behavior
    pattern.lastIndex = 0;
    return pattern.test(text);
};

/**
 * Find all matches of a pattern in text
 * 
 * @param {string} text - Text to search
 * @param {string} patternName - Name of the pattern to use
 * @returns {Array} Array of match objects or empty array
 */
export const findMatches = (text, patternName) => {
    const pattern = getPattern(patternName);
    if (!pattern || !text) return [];
    
    const matches = [];
    pattern.lastIndex = 0; // Reset regex
    
    let match;
    while ((match = pattern.exec(text)) !== null) {
        matches.push({
            text: match[0],
            index: match.index,
            length: match[0].length
        });
    }
    
    return matches;
};

export default TEXT_PATTERNS;
